import { useMemo, useState, useCallback, useRef, useEffect, type RefObject } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { AppLink } from '@/shared/components/AppLink';
import type { AppDispatch } from '@/app/store/store';
import { useAppSelector } from '@/app/store/store';
import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';
import { pdpBuild } from '@/features/pdp/config/pdp.build';
import { customizationBuild } from '@/features/customization/config/customization.build';
import { sizerBuild } from '@/features/sizer/config/sizer.build';
import { similarDesignsBuild, useSimilarDesigns, SimilarDesignsSection } from '@/features/similar-designs';
import { matchingSetsBuild, useMatchingSet, MatchingSetSection } from '@/features/matching-sets';
import { usePdpService } from '@/features/pdp/presentation/hooks/usePdpService';
import { usePdpPresentation } from '@/features/pdp/presentation/hooks/usePdpPresentation';
import { usePdpAnalytics, getDeliveryDaysDifference } from '@/features/pdp/presentation/hooks/usePdpAnalytics';
import { useCustomization } from '@/features/customization/presentation/hooks/useCustomization';
import { usePdpPincodeDelivery } from '@/features/pdp/presentation/hooks/usePdpPincodeDelivery';
import { usePincode } from '@/shared/features/pincode/presentation/hooks/usePincode';
import { useNavigationLoader } from '@/shared/features/navigation-loader';
import { selectIsPincodeConfirmed } from '@/shared/features/pincode/presentation/store/pincodeSlice';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';
import { PdpNearbyStoresModal } from '@/features/pdp/presentation/components/PdpNearbyStoresModal';
import { PdpStoreInventoryModal } from '@/features/pdp/presentation/components/PdpStoreInventoryModal';
import { getDesignIdFromSlug, getCanonicalPathname } from '@/features/pdp/utils/pdpUrl';
import type { PdpSizeOption } from '@/features/pdp/presentation/viewModels/pdp.vm';
import { useWishlistToggle } from '@/features/wishlist/presentation/hooks/useWishlistToggle';
import { PdpBreadCrumbs } from '@/features/pdp/presentation/components/PdpBreadCrumbs';
import { usePdpPosCart } from '@/features/posCart/presentation/hooks/usePdpPosCart';
import StoreSelectionsSection from '@/features/posCart/presentation/components/StoreSelectionsSection';
import { isCurrentProductInPosCart as isCurrentProductInPosCartHelper } from '@/features/posCart/utils/posCartUtils';
import { useAnalytics } from '@/shared/features/analytics/presentation/hooks/useAnalytics';
import { PdpViewStoreSelectionsChevronIcon } from '@/shared/icons/icons';
import { ProductImageGallery } from '@/features/pdp/presentation/components/ProductImageGallery';
import { ProductPriceSummary } from '@/features/pdp/presentation/components/ProductPriceSummary';
import { PdpAdditionalOffer } from '@/features/pdp/presentation/components/PdpAdditionalOffer';
import { VideoCallSchedule } from '@/features/pdp/presentation/components/VideoCallSchedule';
import { PdpDeliveryInfo } from '@/features/pdp/presentation/components/PdpDeliveryInfo';
import { PdpEarlyDelivery } from '@/features/pdp/presentation/components/PdpEarlyDelivery';
import { ProductSpecs } from '@/features/pdp/presentation/components/ProductSpecs';
import { ProductCustomization } from '@/features/customization/presentation/components/ProductCustomization';
import { CustomizationSizeSection } from '@/features/customization/presentation/components/CustomizationSizeSection';
import type { SizeSelectionContext } from '@/features/customization/presentation/hooks/useSizeSelection';
import { shouldShowPdpSizeDropdown, shouldShowPdpSizeSection } from '@/features/customization/utils/shouldShowPdpSizeSection';
import { parseEddSizeList } from '@/features/pdp/utils/parseEddSizeList';
import { getEarlyDeliveryItems } from '@/features/pdp/utils/getEarlyDeliveryItems';
import {
  buildActiveSelectionKey,
  buildEddSpecsFromEarlyDeliveryItem,
} from '@/features/pdp/utils/earlyDeliveryItem.utils';
import type { PdpEddProductSpecs } from '@/features/pdp/domain/models/ResolvedExpectedDeliveryDate';
import type { EarlyDeliveryItem } from '@/features/pdp/domain/models/EarlyDeliveryItem';
import { ProductDimensions } from '@/features/pdp/presentation/components/ProductDimensions';
import { PriceBreakup } from '@/features/pdp/presentation/components/PriceBreakup';
import { BsPromises } from '@/features/pdp/presentation/components/BsPromises';
import { PdpCertifications } from '@/features/pdp/presentation/components/PdpCertifications';
import { CallAndChat } from '@/features/pdp/presentation/components/CallAndChat';
import { ManufacturerInfo } from '@/features/pdp/presentation/components/ManufacturerInfo';
import { SeoSearchLinks } from '@/features/pdp/presentation/components/SeoSearchLinks';
import { BuyNowBar } from '@/features/pdp/presentation/components/BuyNowBar';
import { PdpBuyActions } from '@/features/pdp/presentation/components/PdpBuyActions';
import { PdpDesktopCustomizationAccordion } from '@/features/pdp/presentation/components/PdpDesktopCustomizationAccordion';
import { PdpDesktopSupport } from '@/features/pdp/presentation/components/PdpDesktopSupport';
import { PdpTrustBadges } from '@/features/pdp/presentation/components/PdpDesktopTrustBadges';
import { PdpGalleryCertification } from '@/features/pdp/presentation/components/PdpGalleryCertification';
import { hasCustomizationOptions } from '@/features/customization/utils/parseCustomizationOptions';
import {
  formatProductShortDescriptionLine,
  getEffectiveShortDesc,
} from '@/features/pdp/utils/formatProductShortDescriptionLine';
import { SizePriceTicker } from '@/features/pdp/presentation/components/SizePriceTicker';
import { PdpSectionCard } from '@/features/pdp/presentation/components/PdpSectionCard';
import { PdpDesktopProductDetails } from '@/features/pdp/presentation/components/PdpDesktopProductDetails';
import { PdpProductDetailsAccordionSections } from '@/features/pdp/presentation/components/PdpProductDetailsAccordionSections';
import { PdpDesktopPromiseCard } from '@/features/pdp/presentation/components/PdpDesktopPromiseCard';
import { PdpDesktopProductCode } from '@/features/pdp/presentation/components/PdpDesktopProductCode';
import {
  PdpDesktopDiamondDetailsExpanded,
  PdpDesktopDiamondDetailsSummary,
} from '@/features/pdp/presentation/components/PdpDesktopDiamondDetails';
import { PdpDesktopGemstoneDetails } from '@/features/pdp/presentation/components/PdpDesktopGemstoneDetails';
import {
  PdpDesktopSolitaireDetailsExpanded,
  PdpDesktopSolitaireDetailsSummary,
} from '@/features/pdp/presentation/components/PdpDesktopSolitaireDetails';
import { PdpDesktopMetalDetails } from '@/features/pdp/presentation/components/PdpDesktopMetalDetails';
import { PdpDesktopPriceBreakup } from '@/features/pdp/presentation/components/PdpDesktopPriceBreakup';
import { PdpDesktopTags } from '@/features/pdp/presentation/components/PdpDesktopTags';
import type { PdpDesktopProductDetailsSection } from '@/features/pdp/presentation/components/PdpDesktopProductDetails';
import {
  buildProductStoneDetailsInput,
  getGemstoneDetailSections,
  getGemstoneLines,
  hasDiamondDetails,
  hasSolitaireDetails,
} from '@/features/pdp/utils/productDetailStones';
import { PdpSeoHead } from '@/shared/features/seo/presentation/components/PdpSeoHead';
import { useSizePriceTicker } from '@/features/pdp/presentation/hooks/useSizePriceTicker';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import type { ProductCustomization as AppliedCustomization } from '@/features/customization/domain/models/ProductCustomization';
import { cn } from '@/shared/utils/cn';
import { submitBuyNowForm } from '@/features/pdp/utils/submitBuyNowForm';
import { PdpCartFullPopup } from '@/features/pdp/presentation/components/PdpCartFullPopup';

export default function ProductDetailPage() {
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{ productSlug: string }>();

  const designId = useMemo(
    () => getDesignIdFromSlug(params.productSlug ?? ''),
    [params.productSlug]
  );

  const { service } = useMemo(() => pdpBuild(dispatch), [dispatch]);
  const { service: customizationService } = useMemo(() => customizationBuild(dispatch), [dispatch]);
  const { service: sizerService } = useMemo(() => sizerBuild(dispatch), [dispatch]);
  const { service: similarDesignsService } = useMemo(() => similarDesignsBuild(dispatch), [dispatch]);
  const { service: matchingSetService } = useMemo(() => matchingSetsBuild(dispatch), [dispatch]);
  const { startNavigation, stopNavigation, navigateTo } = useNavigationLoader();

  const validateSizeRef = useRef<(() => boolean) | null>(null);
  const sizePriceTicker = useSizePriceTicker();
  const isMobile = useIsMobile();

  const { productDetail, isLoading } = usePdpService({ designId });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!productDetail) return;
    const canonicalPath =
      getCanonicalPathname(productDetail.canonical) ?? getCanonicalPathname(productDetail.shareUrl);
    if (!canonicalPath || canonicalPath === location.pathname) return;
    navigate(`${canonicalPath}${location.search}${location.hash}`, { replace: true, preventScrollReset: true });
  }, [productDetail, location.pathname, location.search, location.hash, navigate]);
  const { customization, setCustomization } = useCustomization(customizationService);
  const presentation = usePdpPresentation(productDetail, customization);
  const {
    pincode,
    inputValue,
    error: pincodeError,
    handleChange: handlePincodeChange,
    handleSubmit: handlePincodeSubmit,
  } = usePincode();
  const isPincodeConfirmed = useAppSelector(selectIsPincodeConfirmed);
  const { homePageVariant: pageVariant } = useMultivariateExperiment();
  const {
    userContext,
    userCode,
    customerId,
    isLoading: isUserContextLoading,
    isReady: isUserContextReady,
  } = useUserContext();
  const { items: posCartItems, fetchNextPage: fetchNextPosCartPage, hasMore: hasMorePosCart } = usePdpPosCart({
    isLoggedIn: userContext?.isLoggedIn,
    userCode: userCode ?? customerId,
  });

  const isCurrentProductInPosCart = useMemo(() => {
    return isCurrentProductInPosCartHelper(productDetail, posCartItems);
  }, [posCartItems, productDetail]);

  const hasActiveStoreCart = useMemo(() => {
    return posCartItems.filter((item) => item.sourceType === 'POS_CART').length >= 1;
  }, [posCartItems]);

  const { analytics, isInitialized } = useAnalytics();

  const handleScrollToPdpStoreSelections = () => {
    const mobileEl = document.getElementById('pdp-store-selections-section-mobile');
    const desktopEl = document.getElementById('pdp-store-selections-section');
    const element = (mobileEl && mobileEl.offsetHeight > 0) ? mobileEl : desktopEl;

    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      const offsetPosition = elementPosition + scrollTop - headerOffset;
      const scrollOptions: ScrollToOptions = {
        top: offsetPosition,
        behavior: 'smooth',
      };

      try {
        window.scrollTo(scrollOptions);
      } catch (e) {
        console.log(e);
      }

      try {
        document.documentElement.scrollTo(scrollOptions);
      } catch (e) {
        console.log(e);
      }

      try {
        document.body.scrollTo(scrollOptions);
      } catch (e) {
        console.log(e);
      }
    }
    if (analytics && isInitialized && productDetail) {
      analytics.trackEvent('PDP Store Visit Cart CTA Clicked', {
        'Page Name': 'PDP',
        'Product Id': productDetail.designId,
        'Product Name': productDetail.designName,
      });
    }
  };

  const showEarlyDelivery =
    Boolean(pincode && pincode.length === 6) &&
    (isPincodeConfirmed || userContext?.guessedPincode === false);

  const [selectedSize, setSelectedSize] = useState<PdpSizeOption | null>(null);
  const pdpAnalytics = usePdpAnalytics({
    product: productDetail,
    vm: presentation,
    selectedSizeDisplay: selectedSize?.value,
    visitorId:
      userContext?.visitorId != null && Number.isFinite(userContext.visitorId)
        ? String(userContext.visitorId)
        : null,
    isUserContextReady,
  });
  const [isPincodeSubmitting, setIsPincodeSubmitting] = useState(false);
  const [isCartFullPopupOpen, setIsCartFullPopupOpen] = useState(false);
  const [eddSpecsOverride, setEddSpecsOverride] = useState<PdpEddProductSpecs | null>(null);
  const [earlyDeliverySuppressed, setEarlyDeliverySuppressed] = useState(false);
  const [initialSelectionKey, setInitialSelectionKey] = useState<string | null>(null);

  const productSpecs = useMemo(
    () =>
      productDetail && presentation
        ? {
            metal: presentation.metal,
            goldPurity: presentation.goldPurity,
            diamondStoneType: presentation.diamondStoneType,
            solitaireCarat: productDetail.solitaireCarat,
            solitaireClarity: productDetail.solitaireClarity,
            solitaireColor: productDetail.solitaireColor,
            defaultSize: productDetail.size != null ? String(productDetail.size) : null,
          }
        : null,
    [productDetail, presentation]
  );

  const effectiveProductSpecs = useMemo(
    () => eddSpecsOverride ?? productSpecs,
    [eddSpecsOverride, productSpecs]
  );

  const {
    delivery,
    isLoading: isLoadingDelivery,
    deliveryError,
    activeModal,
    openNearbyStoresModal,
    openInventoryModal,
    closeModal,
  } = usePdpPincodeDelivery({
    service,
    pincode,
    designId,
    customizationId: presentation?.activeCustomizationId ?? 0,
    productSpecs: effectiveProductSpecs ?? {
      metal: '',
      goldPurity: null,
      diamondStoneType: null,
      solitaireCarat: null,
      solitaireClarity: null,
      solitaireColor: null,
      defaultSize: null,
    },
    selectedSize: selectedSize?.key ?? null,
  });

  const activeSelectionKey = useMemo(() => {
    if (!productDetail || !presentation) return null;
    return buildActiveSelectionKey({
      customizationId: presentation.activeCustomizationId,
      metal: presentation.metal,
      goldPurity: presentation.goldPurity,
      diamondStoneType: presentation.diamondStoneType,
      solitaireCarat: productDetail.solitaireCarat,
      solitaireClarity: productDetail.solitaireClarity,
      solitaireColor: productDetail.solitaireColor,
      size: selectedSize?.key ?? productDetail.size ?? '',
    });
  }, [productDetail, presentation, selectedSize]);

  const hasCustomizationChanged = useMemo(
    () =>
      Boolean(
        initialSelectionKey &&
          activeSelectionKey &&
          activeSelectionKey !== initialSelectionKey
      ),
    [activeSelectionKey, initialSelectionKey]
  );

  const earlyDeliveryItems = useMemo(() => {
    if (!showEarlyDelivery || !hasCustomizationChanged || earlyDeliverySuppressed) {
      return [];
    }
    return getEarlyDeliveryItems(delivery.designEddDetails, delivery.eddFullDate);
  }, [
    showEarlyDelivery,
    hasCustomizationChanged,
    earlyDeliverySuppressed,
    delivery.designEddDetails,
    delivery.eddFullDate,
  ]);

  useEffect(() => {
    setInitialSelectionKey(null);
    setEddSpecsOverride(null);
    setEarlyDeliverySuppressed(false);
  }, [designId]);

  useEffect(() => {
    if (!productDetail || !presentation || initialSelectionKey !== null) return;
    setInitialSelectionKey(
      buildActiveSelectionKey({
        customizationId: presentation.activeCustomizationId,
        metal: presentation.metal,
        goldPurity: presentation.goldPurity,
        diamondStoneType: presentation.diamondStoneType,
        solitaireCarat: productDetail.solitaireCarat,
        solitaireClarity: productDetail.solitaireClarity,
        solitaireColor: productDetail.solitaireColor,
        size: selectedSize?.key ?? productDetail.size ?? '',
      })
    );
  }, [productDetail, presentation, selectedSize, initialSelectionKey]);

  useEffect(() => {
    setEddSpecsOverride(null);
    setEarlyDeliverySuppressed(false);
  }, [pincode]);

  const { items: similarDesignItems, isLoading: isLoadingSimilar, sentinelRef: similarDesignsSentinelRef } =
    useSimilarDesigns(similarDesignsService, designId);
  const { matchingSet, isLoading: isLoadingMatchingSet, sentinelRef: matchingSetSentinelRef } =
    useMatchingSet(matchingSetService, designId);
  const { isWishlisted, toggleWishlist } = useWishlistToggle();

  const customizationContext = useMemo(
    () =>
      productDetail && presentation
        ? {
            designId,
            designName: productDetail.designName,
            category: productDetail.category,
            customizationOptions: productDetail.customizationOptions,
            metal: presentation.metal,
            goldPurity: presentation.goldPurity,
            diamondStoneType: presentation.diamondStoneType,
            solitaireCarat: productDetail.solitaireCarat,
            solitaireClarity: productDetail.solitaireClarity,
            solitaireColor: productDetail.solitaireColor,
            customizationId: presentation.activeCustomizationId,
            size: productDetail.size,
            discountedPrice: presentation.discountedPrice,
            isSizeable: productDetail.isSizeableProduct,
            nddAvailableSizes: productDetail.nddAvailableSizes,
          }
        : null,
    [designId, productDetail, presentation]
  );

  const handleToggleWishlist = useCallback(async () => {
    if (!productDetail) return;
    const wasWishlisted = isWishlisted(productDetail.designId);
    const success = await toggleWishlist(productDetail.designId);
    if (success) {
      pdpAnalytics.trackWishlistToggle(wasWishlisted ? 'remove' : 'add');
    }
  }, [productDetail, isWishlisted, toggleWishlist, pdpAnalytics]);

  const buildPincodeAnalyticsExtras = useCallback(
    (pinErrorFlag: boolean) => ({
      pinErrorFlag,
      estimatedDeliveryDate: delivery.eddFullDate,
      deliveryTime: getDeliveryDaysDifference(delivery.eddFullDate),
    }),
    [delivery.eddFullDate]
  );

  const handleScheduleVideoCall = useCallback(
    async (customizationId: number) => {
      startNavigation();
      try {
        await service.addToVideoCallCart(customizationId);
        navigateTo('/tah?cartType=videoCallTah');
      } catch (error) {
        stopNavigation();
        throw error;
      }
    },
    [navigateTo, service, startNavigation, stopNavigation]
  );

  const handleCustomizationApplied = useCallback(
    (result: { customization: AppliedCustomization; selectedSize: PdpSizeOption | null }) => {
      setEddSpecsOverride(null);
      setEarlyDeliverySuppressed(false);
      setCustomization(result.customization);
      setSelectedSize(result.selectedSize);
    },
    [setCustomization]
  );

  const handleEarlyDeliveryApplied = useCallback(
    (result: {
      customization: AppliedCustomization;
      selectedSize: PdpSizeOption | null;
      item: EarlyDeliveryItem;
    }) => {
      setCustomization(result.customization);
      setSelectedSize(result.selectedSize);
      setEddSpecsOverride(
        buildEddSpecsFromEarlyDeliveryItem(result.item, productDetail?.size ?? null)
      );
      setEarlyDeliverySuppressed(true);
    },
    [productDetail?.size, setCustomization]
  );

  const handleSizeApplied = useCallback(
    (result: { customization: AppliedCustomization; selectedSize: PdpSizeOption | null }) => {
      handleCustomizationApplied(result);
      if (isMobile) {
        sizePriceTicker.show();
      }
    },
    [handleCustomizationApplied, isMobile, sizePriceTicker]
  );

  const sizeListWithEdd = useMemo(() => {
    if (!productSpecs || !presentation) return [];
    return parseEddSizeList(
      delivery.designEddDetails,
      productSpecs,
      presentation.sizeList
    );
  }, [delivery.designEddDetails, presentation, productSpecs]);

  const effectiveSizeList = useMemo(() => {
    if (sizeListWithEdd.length > 0) return sizeListWithEdd;
    return presentation?.sizeList ?? [];
  }, [sizeListWithEdd, presentation?.sizeList]);

  const sizeSelectionContext = useMemo<SizeSelectionContext | null>(() => {
    if (!productDetail || !presentation) return null;
    return {
      designId,
      metal: presentation.metal,
      goldPurity: presentation.goldPurity,
      diamondStoneType: presentation.diamondStoneType,
      solitaireCarat: productDetail.solitaireCarat,
      solitaireClarity: productDetail.solitaireClarity,
      solitaireColor: productDetail.solitaireColor,
      customizationId: presentation.activeCustomizationId,
      sizes: effectiveSizeList,
    };
  }, [designId, productDetail, presentation, effectiveSizeList]);

  const sizeSectionVisibility = useMemo(
    () =>
      productDetail && presentation
        ? shouldShowPdpSizeSection({
            productSize: productDetail.size,
            isSizeableProduct: productDetail.isSizeableProduct,
            sizeListLength: Math.max(presentation.sizeList.length, effectiveSizeList.length),
            readyToShipQuantity: productDetail.readyToShipQuantity,
          })
        : false,
    [productDetail, presentation, effectiveSizeList.length]
  );

  const needsSizeSelection = useMemo(
    () =>
      productDetail && presentation && sizeSectionVisibility
        ? shouldShowPdpSizeDropdown({
            productSize: productDetail.size,
            isSizeableProduct: productDetail.isSizeableProduct,
            sizeListLength: Math.max(presentation.sizeList.length, effectiveSizeList.length),
            readyToShipQuantity: productDetail.readyToShipQuantity,
          })
        : false,
    [productDetail, presentation, sizeSectionVisibility, effectiveSizeList.length]
  );

  const handleRegisterSizeValidate = useCallback((validate: () => boolean) => {
    validateSizeRef.current = validate;
  }, []);

  const handleBuyNow = useCallback(() => {
    const sizeRequired = productDetail?.isSizeableProduct && !selectedSize;
    if (sizeRequired) {
      validateSizeRef.current?.();
      document.getElementById('pdp-size-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    const isValid = validateSizeRef.current?.() ?? true;
    if (!isValid) {
      document.getElementById('pdp-size-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    if (needsSizeSelection && !selectedSize) {
      validateSizeRef.current?.();
      document.getElementById('pdp-size-section')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    pdpAnalytics.trackAddToCart();
    const cartCount = userContext?.cartCount ?? 0;
    const maxCartItems = userContext?.maxCartItemsCount ?? 3;
    if (isUserContextLoading || cartCount >= maxCartItems) {
      setIsCartFullPopupOpen(true);
      return;
    }
    if (!productDetail || !presentation) return;
    const sizeSelected = productDetail.isSizeableProduct
      ? (selectedSize?.key ?? '')
      : (productDetail.size ?? '');
    submitBuyNowForm({
      customizationId: presentation.activeCustomizationId,
      sizeSelected,
      discountedPrice: presentation.discountedPrice,
    });
  }, [needsSizeSelection, selectedSize, pdpAnalytics, userContext, isUserContextLoading, productDetail, presentation]);

  const handlePincodeUpdate = useCallback(async () => {
    const submittedPincode = inputValue.trim();
    setIsPincodeSubmitting(true);
    try {
      const success = await handlePincodeSubmit();
      if (success && submittedPincode) {
        pdpAnalytics.trackPincodeChecked(submittedPincode, {
          ...buildPincodeAnalyticsExtras(false),
          broadCity: delivery.cityName,
          nearbyStoreAvailable: delivery.nearbyStoreCount > 0,
        });
      }
      return success;
    } finally {
      setIsPincodeSubmitting(false);
    }
  }, [handlePincodeSubmit, inputValue, pdpAnalytics, buildPincodeAnalyticsExtras, delivery.cityName, delivery.nearbyStoreCount]);

  const handleViewAllDesigns = useCallback(() => {
    pdpAnalytics.trackDesignsInStoresClick();
    openNearbyStoresModal();
  }, [openNearbyStoresModal, pdpAnalytics]);

  const handleViewInventory = useCallback(() => {
    pdpAnalytics.trackAvailableStoresClick(delivery.inventoryStores);
    openInventoryModal();
  }, [openInventoryModal, pdpAnalytics, delivery.inventoryStores]);

  useEffect(() => {
    const handlePageHide = () => {
      const navEntries = performance.getEntriesByType('navigation');
      const navType =
        navEntries.length > 0 ? (navEntries[0] as PerformanceNavigationTiming).type : '';
      if (navType === 'reload') return;

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const scrollPercent =
        docHeight > windowHeight
          ? Math.round((scrollTop / (docHeight - windowHeight)) * 100)
          : 0;
      pdpAnalytics.trackMaxScrollView(scrollPercent);
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [designId, pdpAnalytics]);

  const hasTags =
    productDetail?.tags != null &&
    typeof productDetail.tags === 'object' &&
    Object.keys(productDetail.tags as object).length > 0;

  const stoneDetailsInput = useMemo(
    () =>
      productDetail && presentation
        ? buildProductStoneDetailsInput(productDetail, customization, presentation.diamondStoneType)
        : null,
    [productDetail, customization, presentation]
  );

  const desktopProductDetailSections = useMemo<PdpDesktopProductDetailsSection[]>(() => {
    if (!productDetail || !presentation || !stoneDetailsInput) return [];
    const gemstoneSections = getGemstoneDetailSections(stoneDetailsInput);
    const onInfoIconClick = pdpAnalytics.trackProductDetailsInfoIconViewed;
    return [
      ...(hasDiamondDetails(stoneDetailsInput)
        ? [
            {
              id: 'diamond-details',
              title: 'Diamond Details',
              content: (
                <>
                  <PdpDesktopDiamondDetailsSummary
                    {...stoneDetailsInput}
                    onInfoIconClick={onInfoIconClick}
                  />
                  <PdpDesktopDiamondDetailsExpanded {...stoneDetailsInput} />
                </>
              ),
            },
          ]
        : []),
      ...gemstoneSections.map((section, index) => ({
        id: `gemstone-details-${index}`,
        title: `${section.type} Details`,
        content: <PdpDesktopGemstoneDetails gem={section.gem} specs={section.specs} />,
      })),
      ...(hasSolitaireDetails(stoneDetailsInput)
        ? [
            {
              id: 'solitaire-details',
              title: 'Solitaire Details',
              content: (
                <>
                  <PdpDesktopSolitaireDetailsSummary
                    {...stoneDetailsInput}
                    onInfoIconClick={onInfoIconClick}
                  />
                  <PdpDesktopSolitaireDetailsExpanded {...stoneDetailsInput} />
                </>
              ),
            },
          ]
        : []),
      ...(presentation.goldPurity || presentation.metalWeight > 0
        ? [
            {
              id: 'metal-details',
              title: 'Metal Details',
              content: (
                <PdpDesktopMetalDetails
                  goldPurity={presentation.goldPurity}
                  metal={presentation.metal}
                  metalType={presentation.metalType}
                  shortDesc={productDetail.shortDesc}
                  metalWeight={presentation.metalWeight}
                  onInfoIconClick={onInfoIconClick}
                />
              ),
            },
          ]
        : []),
      ...(!productDetail.hideBreakUp && presentation.price
        ? [
            {
              id: 'price-breakup',
              title: 'Price Breakup',
              content: (
                <PdpDesktopPriceBreakup
                  goldPrice={presentation.goldPrice}
                  diamondPrice={presentation.diamondPrice}
                  diamondPriceAfterDiscount={presentation.diamondPriceAfterDiscount}
                  discountOnDiamondPriceValue={presentation.discountOnDiamondPriceValue}
                  gemstonePrice={presentation.gemstonePrice}
                  presetSolitairePrice={presentation.presetSolitairePrice}
                  makingChargePrice={presentation.makingChargePrice}
                  makingChargePriceAfterDiscount={presentation.makingChargePriceAfterDiscount}
                  makingChargeDiscountValue={presentation.makingChargeDiscountValue}
                  gst={presentation.gst}
                  price={presentation.price}
                  discountedPrice={presentation.discountedPrice}
                  discountValue={presentation.discountValue}
                  flatDiscountPercent={presentation.flatDiscountPercent}
                  metalType={presentation.metalType}
                  gemstonesTypeAndCount={presentation.gemstonesTypeAndCount}
                  stoneSpecifications={stoneDetailsInput.stoneSpecifications}
                />
              ),
            },
          ]
        : []),
      ...(hasTags
        ? [
            {
              id: 'tags',
              title: 'Tags',
              content: <PdpDesktopTags tags={productDetail.tags} />,
            },
          ]
        : []),
    ];
  }, [productDetail, presentation, stoneDetailsInput, hasTags, pdpAnalytics.trackProductDetailsInfoIconViewed]);

  const mobileProductDetailSections = useMemo(
    () =>
      desktopProductDetailSections.filter(
        (section) => section.id !== 'price-breakup' && section.id !== 'tags'
      ),
    [desktopProductDetailSections]
  );

  const productUrl =
    productDetail?.shareUrl ??
    (productDetail?.canonical?.replace('__BASE_URL__', typeof window !== 'undefined' ? window.location.origin : '') ??
      '');

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f7f7]">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
      </div>
    );
  }

  if (!productDetail || !presentation || !stoneDetailsInput) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f7f7]">
        <p className="text-sm text-gray-500">Product not found.</p>
      </div>
    );
  }

  const wishlisted = isWishlisted(productDetail.designId);
  const isSilver = presentation.metalType === 'Silver';
  const showCustomization = hasCustomizationOptions(productDetail.customizationOptions);
  const effectiveShortDesc = getEffectiveShortDesc(productDetail.shortDesc, customization?.shortDesc);
  const shortDescLine = formatProductShortDescriptionLine({
    shortDesc: effectiveShortDesc,
    metalWeight: presentation.metalWeight,
    diamondCarat: stoneDetailsInput.diamondCarat,
  });
  const pdpHeroFlatClass = 'md:rounded-none md:bg-transparent md:shadow-none';
  const showMatchingSetSection =
    isLoadingMatchingSet ||
    (matchingSet?.items ?? []).filter((item) => item.designId !== productDetail.designId).length > 1;
  const showSimilarDesignsSection =
    isLoadingSimilar ||
    (similarDesignItems ?? []).filter((item) => item.designId !== productDetail.designId).length > 0;

  return (
    <div className="min-h-screen bg-[#f7f7f7] pb-4 md:bg-white md:pb-8">
      {isMobile && sizePriceTicker.isVisible && selectedSize && (
        <SizePriceTicker
          ref={sizePriceTicker.tickerRef}
          active={sizePriceTicker.isActive}
          category={productDetail.category}
          selectedSizeValue={selectedSize.value}
          price={presentation.price}
          discountedPrice={presentation.discountedPrice}
        />
      )}
      <div className="mx-auto max-w-7xl">
        <PdpSeoHead productDetail={productDetail} />
      </div>

      <div className="md:bg-[#f0f0f0] md:mb-8">
        <div className="mx-auto max-w-7xl">
          <div className="hidden px-4 pt-4 md:block">
            <PdpBreadCrumbs schema={productDetail.breadCrumbListSchema} />
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-8 md:px-4 md:py-6">
            <div className="w-full md:sticky md:top-4 md:w-[50%]">
              <ProductImageGallery
                mediaItems={productDetail.mediaItems}
                designName={productDetail.designName}
                designId={productDetail.designId}
                isWishlisted={wishlisted}
                onAddToWishlist={handleToggleWishlist}
                onRemoveFromWishlist={handleToggleWishlist}
                shareUrl={productUrl}
                onMediaIndexChange={pdpAnalytics.trackMediaViewed}
                onMediaThumbnailClick={pdpAnalytics.trackMediaThumbnailClick}
                onMediaThumbnailScroll={pdpAnalytics.trackMediaThumbnailScrolled}
                onVideoPlay={pdpAnalytics.trackPdpVideoPlayed}
                onShareClick={pdpAnalytics.trackProductShareClick}
              />
              <PdpGalleryCertification
                metalType={presentation.metalType}
                diamondStoneType={presentation.diamondStoneType}
                solitaireTotalNoOfStones={productDetail.solitaireTotalNoOfStones}
                onCertificationClick={pdpAnalytics.trackCertificationClick}
              />
            </div>
            {userContext?.isLoggedIn && hasActiveStoreCart && pageVariant === 'V4' && (
              <div className="md:hidden px-3">
                <button
                  type="button"
                  onClick={handleScrollToPdpStoreSelections}
                  className="w-full flex items-center justify-center gap-1.5 py-2 px-4 bg-[linear-gradient(90deg,_#C9A961_0%,_#E4D4B0_100%)] text-white font-bold text-[14px] sm:text-[15px] shadow-[0_2px_4px_rgba(0,0,0,0.06)] hover:from-[#C5A259] hover:to-[#D7B56D] active:scale-[0.99] transition-all duration-200 border border-[#C5A880]/40"
                >
                  <span>
                    {isCurrentProductInPosCart
                      ? "Found in your Store-Visit Selections"
                      : "View your In-Store Selections"}
                  </span>
                  <PdpViewStoreSelectionsChevronIcon className="h-4 w-4 shrink-0" />
                </button>
              </div>
            )}
            <div className="flex flex-col gap-3 px-3 md:w-[50%] md:gap-5 md:px-0">
              <div className="flex flex-col md:gap-[10px]">
                <h1 className="hidden font-primary text-[26px] font-bold leading-tight text-black md:block">
                  {productDetail.designName}
                </h1>
                {productDetail.collectionName && productDetail.collectionPageUrl ? (
                  <p className="hidden text-sm leading-snug text-black md:block">
                    From{' '}
                    <AppLink
                      to={productDetail.collectionPageUrl}
                      className="font-bold text-[var(--link-blue)] no-underline hover:underline"
                    >
                      {productDetail.collectionName}
                    </AppLink>
                  </p>
                ) : null}

                <PdpSectionCard className={cn('overflow-hidden p-0', pdpHeroFlatClass)}>
                <ProductPriceSummary
                  price={presentation.price}
                  discountedPrice={presentation.discountedPrice}
                  discountValue={presentation.discountValue}
                  discountName={presentation.discountName}
                  disclaimer={productDetail.disclaimer}
                />
                <PdpAdditionalOffer
                  text={productDetail.additionalDiscountText}
                  onTncClick={pdpAnalytics.trackDiscountTncClick}
                />
                <VideoCallSchedule
                  customizationId={presentation.activeCustomizationId}
                  onScheduleVideoCall={handleScheduleVideoCall}
                  onBookNowClick={pdpAnalytics.trackVideoCallCartAdd}
                />
                <PdpDeliveryInfo
                  inputValue={inputValue}
                  confirmedPincode={pincode}
                  pincodeError={pincodeError}
                  deliveryError={deliveryError}
                  isLoadingDelivery={isLoadingDelivery || isPincodeSubmitting}
                  delivery={delivery}
                  designName={productDetail.designName}
                  productUrl={productUrl}
                  onInputChange={handlePincodeChange}
                  onUpdate={handlePincodeUpdate}
                  onViewAllDesigns={handleViewAllDesigns}
                  onViewInventory={handleViewInventory}
                  onPincodeUpdateClick={(value) => {
                    const pinErrorFlag = value.length !== 6 || Boolean(pincodeError);
                    pdpAnalytics.trackPincodeEntered(value, buildPincodeAnalyticsExtras(pinErrorFlag));
                  }}
                />
                {earlyDeliveryItems.length > 0 ? (
                  <div className="border-t border-gray-200 px-4 pb-4 md:px-0">
                    <PdpEarlyDelivery
                      items={earlyDeliveryItems}
                      designId={designId}
                      service={customizationService}
                      onApplied={handleEarlyDeliveryApplied}
                    />
                  </div>
                ) : null}
                {shortDescLine ? (
                  <p
                    id="short_desc_goldWt"
                    className="hidden border-t border-gray-200 px-4 py-4 text-[13px] leading-snug text-[#666] md:block md:border-0 md:px-0 md:py-0 md:font-secondary md:text-sm md:font-bold md:leading-5 md:text-[#666]"
                  >
                    {shortDescLine}
                  </p>
                ) : null}
              </PdpSectionCard>
              </div>

              {!isSilver ? <PdpTrustBadges variant="mobile" /> : null}

              {showCustomization ? (
                <PdpDesktopCustomizationAccordion
                  service={customizationService}
                  context={
                    customizationContext
                      ? {
                          designId: customizationContext.designId,
                          designName: customizationContext.designName,
                          customizationOptions: customizationContext.customizationOptions,
                          metal: customizationContext.metal,
                          goldPurity: customizationContext.goldPurity,
                          diamondStoneType: customizationContext.diamondStoneType,
                          solitaireCarat: customizationContext.solitaireCarat,
                          solitaireClarity: customizationContext.solitaireClarity,
                          solitaireColor: customizationContext.solitaireColor,
                          customizationId: customizationContext.customizationId,
                          size: customizationContext.size,
                          discountedPrice: customizationContext.discountedPrice,
                        }
                      : null
                  }
                  selectedSize={selectedSize}
                  onApplied={handleCustomizationApplied}
                  onCustomizeClick={pdpAnalytics.trackCustomizationClick}
                  onCustomizationSelected={pdpAnalytics.trackCustomizationSelected}
                />
              ) : null}

              <ProductCustomization
                service={customizationService}
                context={customizationContext}
                selectedSize={selectedSize}
                onApplied={handleCustomizationApplied}
                onCustomizeOpen={pdpAnalytics.trackCustomizationClick}
                onCustomizationSelected={pdpAnalytics.trackCustomizationSelected}
              >
                {(openCustomize) => (
                  <PdpSectionCard className="md:hidden">
                    <ProductSpecs
                      designName={productDetail.designName}
                      collectionName={productDetail.collectionName}
                      collectionPageUrl={productDetail.collectionPageUrl}
                      metal={presentation.metal}
                      metalType={presentation.metalType}
                      goldPurity={presentation.goldPurity}
                      metalWeight={presentation.metalWeight}
                      diamondCarat={stoneDetailsInput.diamondCarat}
                      diamondStoneType={stoneDetailsInput.diamondStoneType}
                      solitaireCarat={stoneDetailsInput.solitaireCarat}
                      solitaireClarity={stoneDetailsInput.solitaireClarity}
                      solitaireColor={stoneDetailsInput.solitaireColor}
                      shortDescLine={shortDescLine}
                      customizationOptions={productDetail.customizationOptions}
                      onCustomize={openCustomize}
                      onViewMoreDetailsClick={pdpAnalytics.trackViewMoreDetailsClick}
                    />
                  </PdpSectionCard>
                )}
              </ProductCustomization>

              {sizeSectionVisibility ? (
                <PdpSectionCard className={cn('overflow-hidden p-0 md:overflow-visible', pdpHeroFlatClass)}>
                  <CustomizationSizeSection
                    service={customizationService}
                    sizerService={sizerService}
                    context={sizeSelectionContext}
                    sizes={effectiveSizeList}
                    selectedSize={selectedSize}
                    category={productDetail.category}
                    productSize={productDetail.size}
                    isSizeable={productDetail.isSizeableProduct}
                    nddAvailableSizes={productDetail.nddAvailableSizes}
                    readyToShipQuantity={productDetail.readyToShipQuantity}
                    hasPincode={Boolean(pincode && pincode.length === 6)}
                    customizationId={presentation.activeCustomizationId}
                    onApplied={handleSizeApplied}
                    onRegisterValidate={handleRegisterSizeValidate}
                    onSizeSelected={pdpAnalytics.trackSizeSelected}
                    onFreeSizerClick={pdpAnalytics.trackFreeSizerClick}
                    onFreeSizerDetailsAdded={pdpAnalytics.trackFreeSizerDetailsAdded}
                    onResendOtp={pdpAnalytics.trackResendOtp}
                    onOtpVerified={pdpAnalytics.trackOtpVerified}
                  />
                </PdpSectionCard>
              ) : null}

              <div className="hidden flex-col gap-3 md:flex">
                <PdpBuyActions
                  variant="inline"
                  discountedPrice={presentation.discountedPrice}
                  gmsFirstInstallmentDiscountEnabled={productDetail.gmsFirstInstallmentDiscountEnabled}
                  gmsFirstInstallmentDiscountPercentage={productDetail.gmsFirstInstallmentDiscountPercentage}
                  onBuyNow={handleBuyNow}
                  onBuyNowClick={pdpAnalytics.trackBuyNow}
                  onGmsPlanOpen={pdpAnalytics.trackGmsPlanClick}
                  onGmsSubscribeClick={pdpAnalytics.trackGmsSubscribeClick}
                  onGmsTncClick={pdpAnalytics.trackGmsTncClick}
                />
                {!isSilver ? <PdpTrustBadges variant="desktop" /> : null}
                <PdpDesktopSupport onContactClick={pdpAnalytics.trackContactButtonClick} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl">
        <div className="h-0 overflow-hidden" aria-hidden>
          <div ref={matchingSetSentinelRef as RefObject<HTMLDivElement>} />
          <div ref={similarDesignsSentinelRef as RefObject<HTMLDivElement>} />
        </div>


        
        <PdpNearbyStoresModal
          open={activeModal === 'nearby'}
          onOpenChange={(open) => !open && closeModal()}
          stores={delivery.nearbyStoresList}
          onStoreSelected={pdpAnalytics.trackStoreSelected}
        />
        <PdpStoreInventoryModal
          open={activeModal === 'inventory'}
          onOpenChange={(open) => !open && closeModal()}
          stores={delivery.inventoryStores}
        />

        <div className="flex flex-col gap-3 md:pb-8 md:pt-4">
          <PdpSectionCard className="p-0 md:hidden mx-3 md:mx-4">
            <PdpCertifications
              metalType={presentation.metalType}
              diamondStoneType={presentation.diamondStoneType}
              solitaireTotalNoOfStones={productDetail.solitaireTotalNoOfStones}
              onCertificationClick={pdpAnalytics.trackCertificationClick}
            />
          </PdpSectionCard>

          

          <PdpSectionCard id="product-details" className="md:hidden mx-3 md:mx-4">
            <ProductDimensions
              productCode={productDetail.productCode}
              productHeight={productDetail.productHeight}
              productWidth={productDetail.productWidth}
              productLength={productDetail.productLength}
              productWeight={presentation.productWeight}
              metalWeight={presentation.metalWeight}
              diamondCarat={stoneDetailsInput.diamondCarat}
              noOfDiamonds={stoneDetailsInput.noOfDiamonds}
              diamondStoneType={stoneDetailsInput.diamondStoneType}
              solitaireCarat={stoneDetailsInput.solitaireCarat}
              solitaireClarity={stoneDetailsInput.solitaireClarity}
              solitaireColor={stoneDetailsInput.solitaireColor}
              solitaireTotalNoOfStones={stoneDetailsInput.solitaireTotalNoOfStones}
              solitaireShape={stoneDetailsInput.solitaireShape}
              gemstones={stoneDetailsInput.gemstones}
              gemstonesTypeAndCount={stoneDetailsInput.gemstonesTypeAndCount}
              stoneSpecifications={stoneDetailsInput.stoneSpecifications}
              goldPurity={presentation.goldPurity}
              metal={presentation.metal}
              metalType={presentation.metalType}
              shortDesc={productDetail.shortDesc}
              onSectionViewed={pdpAnalytics.trackProductDetailsViewed}
              onInfoIconClick={pdpAnalytics.trackProductDetailsInfoIconViewed}
            />
          </PdpSectionCard>

          {mobileProductDetailSections.length > 0 ? (
            <PdpSectionCard className="overflow-hidden p-0 md:hidden mx-3 md:mx-4">
              <PdpProductDetailsAccordionSections
                sections={mobileProductDetailSections}
                expandByDefault={false}
                className="[&>div:first-child>button]:border-t-0"
              />
            </PdpSectionCard>
          ) : null}

          {!productDetail.hideBreakUp ? (
            <PdpSectionCard className="md:hidden mx-3 md:mx-4">
              <PriceBreakup
                goldPrice={presentation.goldPrice}
                diamondPrice={presentation.diamondPrice}
                diamondPriceAfterDiscount={presentation.diamondPriceAfterDiscount}
                discountOnDiamondPriceValue={presentation.discountOnDiamondPriceValue}
                gemstonePrice={presentation.gemstonePrice}
                presetSolitairePrice={presentation.presetSolitairePrice}
                makingChargePrice={presentation.makingChargePrice}
                makingChargePriceAfterDiscount={presentation.makingChargePriceAfterDiscount}
                makingChargeDiscountValue={presentation.makingChargeDiscountValue}
                gst={presentation.gst}
                price={presentation.price}
                discountedPrice={presentation.discountedPrice}
                discountValue={presentation.discountValue}
                discountPercent={presentation.discountPercent}
                discountName={presentation.discountName}
                flatDiscountPercent={presentation.flatDiscountPercent}
                metalType={presentation.metalType}
                hideBreakUp={productDetail.hideBreakUp}
                gemstonesTypeAndCount={presentation.gemstonesTypeAndCount}
                stoneSpecifications={stoneDetailsInput.stoneSpecifications}
              />
            </PdpSectionCard>
          ) : null}

          {userContext?.isLoggedIn && hasActiveStoreCart && (pageVariant === 'V4' || pageVariant === 'V5') && (
            <div id="pdp-store-selections-section-mobile" className="md:hidden">
              <StoreSelectionsSection items={posCartItems} pageName="PDP" onLoadMore={fetchNextPosCartPage} hasMore={hasMorePosCart} />
            </div>
          )}

          {showSimilarDesignsSection ? (
            <PdpSectionCard className="md:hidden mx-3 md:mx-4">
              <SimilarDesignsSection
                items={similarDesignItems}
                isLoading={isLoadingSimilar}
                currentDesignId={productDetail.designId}
                onDesignClick={pdpAnalytics.trackSimilarDesignSelected}
              />
            </PdpSectionCard>
          ) : null}

          {showMatchingSetSection ? (
            <PdpSectionCard className="md:hidden mx-3 md:mx-4">
              <MatchingSetSection
                matchingSet={matchingSet}
                isLoading={isLoadingMatchingSet}
                currentDesignId={productDetail.designId}
                onItemClick={pdpAnalytics.trackFrequentlyBoughtItemSelected}
              />
            </PdpSectionCard>
          ) : null}

          {/* Desktop two-column layout for bottom section — 2 : 1.3 ratio via flex grow */}
          <div className="hidden w-full md:flex md:items-start md:gap-4 md:pt-4">
            <div className="min-w-0 basis-0 md:flex-[20]">
              {userContext?.isLoggedIn && hasActiveStoreCart && pageVariant === 'V4' && (
                <div className="w-full mb-4">
                  <button
                    type="button"
                    onClick={handleScrollToPdpStoreSelections}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 px-4 bg-[linear-gradient(90deg,_#C9A961_0%,_#E4D4B0_100%)] text-white font-bold text-[14px] sm:text-[15px] shadow-[0_2px_4px_rgba(0,0,0,0.06)] hover:from-[#C5A259] hover:to-[#D7B56D] active:scale-[0.99] transition-all duration-200 border border-[#C5A880]/40 rounded-[2px]"
                  >
                    <span>
                      {isCurrentProductInPosCart
                        ? "Found in your Store-Visit Selections"
                        : "View your In-Store Selections"}
                    </span>
                    <PdpViewStoreSelectionsChevronIcon className="h-4 w-4 shrink-0" />
                  </button>
                </div>
              )}
              <PdpDesktopProductDetails
                topRows={
                  <PdpDesktopProductCode
                    productCode={productDetail.productCode}
                    productHeight={productDetail.productHeight}
                    productWidth={productDetail.productWidth}
                    productWeight={presentation.productWeight}
                    showProductWeight={getGemstoneLines(stoneDetailsInput).length === 0}
                    onInfoIconClick={pdpAnalytics.trackProductDetailsInfoIconViewed}
                  />
                }
                sections={desktopProductDetailSections}
                onProductDetailsViewed={pdpAnalytics.trackProductDetailsViewed}
              />
              <ManufacturerInfo variant="desktop" className="mt-4" />
            </div>
            <div className="min-w-0 basis-0 md:flex-[13]">
              <PdpDesktopPromiseCard
                onKnowMoreClick={pdpAnalytics.trackKnowMoreClick}
                showPromise={!isSilver}
              />
            </div>
          </div>

          {/* Desktop-only Selections, Similar Designs, Matching Set and Customer Speak! */}
          {userContext?.isLoggedIn && hasActiveStoreCart && (pageVariant === 'V4' || pageVariant === 'V5') && (
            <div id="pdp-store-selections-section" className="hidden md:block w-full">
              <StoreSelectionsSection items={posCartItems} pageName="PDP" onLoadMore={fetchNextPosCartPage} hasMore={hasMorePosCart} />
            </div>
          )}

          {showSimilarDesignsSection ? (
            <div className="hidden md:block w-full">
              <SimilarDesignsSection
                items={similarDesignItems}
                isLoading={isLoadingSimilar}
                currentDesignId={productDetail.designId}
                onDesignClick={pdpAnalytics.trackSimilarDesignSelected}
              />
            </div>
          ) : null}

          {showMatchingSetSection ? (
            <div className="hidden md:block w-full pb-8">
              <MatchingSetSection
                matchingSet={matchingSet}
                isLoading={isLoadingMatchingSet}
                currentDesignId={productDetail.designId}
                onItemClick={pdpAnalytics.trackFrequentlyBoughtItemSelected}
              />
            </div>
          ) : null}



          {hasTags ? (
            <SeoSearchLinks
              tags={productDetail.tags}
              variant="desktop"
              className="hidden md:block text-sm mt-4"
              onTagClick={pdpAnalytics.trackOtherDesignSelected}
            />
          ) : null}

          {!isSilver ? (
            <PdpSectionCard className="p-0 md:hidden mx-3 md:mx-4">
              <BsPromises onKnowMoreClick={pdpAnalytics.trackKnowMoreClick} />
            </PdpSectionCard>
          ) : null}

          <PdpSectionCard className="p-0 md:hidden mx-3 md:mx-4">
            <CallAndChat onContactClick={pdpAnalytics.trackContactButtonClick} />
          </PdpSectionCard>

          <PdpSectionCard className="px-[15px] py-3 md:hidden mx-3 md:mx-4">
            <ManufacturerInfo />
          </PdpSectionCard>

          {hasTags && (
            <PdpSectionCard className="p-0 md:hidden mx-3 md:mx-4">
              <SeoSearchLinks tags={productDetail.tags} onTagClick={pdpAnalytics.trackOtherDesignSelected} />
            </PdpSectionCard>
          )}

          {/* <PdpSectionCard>
            <ProductFaq />
          </PdpSectionCard> */}
        </div>

        <BuyNowBar
          discountedPrice={presentation.discountedPrice}
          gmsFirstInstallmentDiscountEnabled={productDetail.gmsFirstInstallmentDiscountEnabled}
          gmsFirstInstallmentDiscountPercentage={productDetail.gmsFirstInstallmentDiscountPercentage}
          onBuyNow={handleBuyNow}
          onBuyNowClick={pdpAnalytics.trackBuyNow}
          onGmsPlanOpen={pdpAnalytics.trackGmsPlanClick}
          onGmsSubscribeClick={pdpAnalytics.trackGmsSubscribeClick}
          onGmsTncClick={pdpAnalytics.trackGmsTncClick}
        />

        <PdpCartFullPopup
          open={isCartFullPopupOpen}
          cartCount={userContext?.cartCount ?? 0}
          maxCartItemsCount={userContext?.maxCartItemsCount ?? 30}
          isWishlisted={wishlisted}
          hasLocalOverlayOpen={activeModal !== null}
          onClose={() => setIsCartFullPopupOpen(false)}
          onAddToWishlist={() => void handleToggleWishlist()}
        />
      </div>
    </div>
  );
}
